<template>
  <div class="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 sm:px-6 lg:py-12">
    <CompanyNavigation v-if="route.name === 'CompanyHolidaysPage'" :company-uuid="companyUuid" :company-name="store.company?.name" />
    <header class="flex flex-col gap-3 border-b pb-6">
      <div class="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-300">
        <CalendarDays class="h-5 w-5" />
        Centratel holiday scheduling
      </div>
      <h1 class="text-3xl font-semibold tracking-tight sm:text-4xl">
        {{ heading }}
      </h1>
      <p class="max-w-3xl text-sm leading-6 text-muted-foreground sm:text-base">
        Confirm that your business will be closed, or choose an opening schedule and closing time for each holiday.
      </p>
    </header>

    <Alert v-if="store.error" variant="destructive">
      <CircleAlert class="h-4 w-4" />
      <AlertTitle>Schedule unavailable</AlertTitle>
      <AlertDescription>{{ store.error }}</AlertDescription>
    </Alert>

    <Alert v-if="hasLockedHolidays">
      <LockKeyhole class="h-4 w-4" />
      <AlertTitle>Some holidays are locked</AlertTitle>
      <AlertDescription>Contact Centratel if a locked holiday schedule needs to be changed.</AlertDescription>
    </Alert>

    <div v-if="store.isLoading && store.items.length === 0" class="flex min-h-52 items-center justify-center gap-3 rounded-xl border bg-card text-sm text-muted-foreground">
      <LoaderCircle class="h-5 w-5 animate-spin" />
      Loading your holiday schedule…
    </div>

    <HolidaySchedule v-else-if="store.items.length > 0" :holidays="store.items" :saving-ids="store.savingIds" @confirm-defaults="confirmDefaults" @edit="editHoliday" />

    <div v-else-if="!store.error" class="rounded-xl border border-dashed bg-card px-6 py-14 text-center">
      <CalendarCheck class="mx-auto h-9 w-9 text-muted-foreground" />
      <div class="flex flex-col gap-1">
        <h2 class="font-medium">
          No upcoming holidays
        </h2>
        <p class="text-sm text-muted-foreground">
          There is nothing to confirm right now.
        </p>
      </div>
    </div>

    <div v-if="confirmableHolidays.length > 0" class="sticky bottom-4 flex justify-center">
      <Button type="button" size="lg" class="h-auto min-h-12 w-full gap-2 whitespace-normal py-3 shadow-lg sm:w-auto" :loading="store.isLoading" @click="confirmAll">
        <CheckCheck class="h-4 w-4" />
        Confirm all {{ confirmableHolidays.length }} remaining holidays
      </Button>
    </div>

    <Dialog :open="isEditDialogOpen" @update:open="handleEditDialogOpen">
      <DialogContent class="max-h-[90vh] max-w-xl overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Edit holiday schedule</DialogTitle>
          <DialogDescription>Choose the schedule the customer would submit for this holiday.</DialogDescription>
        </DialogHeader>
        <HolidayScheduleCard v-if="selectedHoliday" :key="selectedHoliday.holiday_id" :holiday="selectedHoliday" :saving="store.savingIds.includes(selectedHoliday.holiday_id)" @save="saveHoliday" />
      </DialogContent>
    </Dialog>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { toast } from 'vue-sonner'
import { CalendarCheck, CalendarDays, CheckCheck, CircleAlert, LoaderCircle, LockKeyhole } from 'lucide-vue-next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import HolidayScheduleCard from '../components/HolidayScheduleCard.vue'
import CompanyNavigation from '../components/CompanyNavigation.vue'
import HolidaySchedule from '../components/HolidaySchedule.vue'
import { useHolidaysPublicStore } from '../stores/holidaysPublicStore.js'

const route = useRoute()
const store = useHolidaysPublicStore()
const companyUuid = computed(() => String(route.params.companyUuid ?? ''))
const heading = computed(() => (store.company?.name ? `Holiday schedule for ${store.company.name}` : 'Your holiday schedule'))
const hasLockedHolidays = computed(() => store.items.some(holiday => holiday.locked))
const confirmableHolidays = computed(() => store.items.filter(holiday => !holiday.locked && !holiday.confirmed))
const selectedHoliday = ref(null)
const isEditDialogOpen = ref(false)

const loadSchedule = async () => {
  if (!companyUuid.value) {
    store.error = 'This holiday schedule link is incomplete.'

    return
  }

  try {
    await store.fetchSchedule(companyUuid.value)
  } catch {
  }
}

const saveHoliday = async ({ ciaHolidayId, schedule }) => {
  try {
    await store.saveHoliday(companyUuid.value, ciaHolidayId, schedule)
    toast.success('Holiday schedule confirmed.')
    isEditDialogOpen.value = false
    selectedHoliday.value = null
  } catch {
    toast.error(store.error)
  }
}

const confirmDefaults = async holiday => {
  try {
    await store.saveHoliday(companyUuid.value, holiday.holiday_id, {
      open: false,
      closing_at: null,
      notes: null,
    })
    toast.success(`${holiday.name} confirmed with the default closed schedule.`)
  } catch {
    toast.error(store.error)
  }
}

const confirmAll = async () => {
  try {
    await store.confirmAll(companyUuid.value, confirmableHolidays.value.map(holiday => holiday.holiday_id))
    toast.success('All remaining holiday schedules were confirmed with their defaults.')
  } catch {
    toast.error(store.error)
  }
}

const editHoliday = holiday => {
  selectedHoliday.value = holiday
  isEditDialogOpen.value = true
}

const handleEditDialogOpen = open => {
  isEditDialogOpen.value = open

  if (!open) {
    selectedHoliday.value = null
  }
}

onMounted(loadSchedule)
watch(companyUuid, loadSchedule)
</script>
